import React, { useState, useEffect, useContext } from 'react';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Search, 
  CheckCircle, 
  Clock, 
  User, 
  RotateCcw,
  Play,
  Square,
  Settings,
  Eye,
  EyeOff
} from 'lucide-react';

const categoryColors = {
  'BUILDERS': { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', header: 'bg-amber-500' },
  'LIFESTYLES': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', header: 'bg-green-500' },
  'IN THE HOME': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300', header: 'bg-blue-500' },
  'SUPPORT': { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300', header: 'bg-purple-500' }
};

const Verifications = () => {
  const { user } = useContext(AuthContext);
  const [departments, setDepartments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [rosters, setRosters] = useState([]);
  const [settings, setSettings] = useState({ round_3_enabled: true });
  const [selectedRound, setSelectedRound] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [assignDialog, setAssignDialog] = useState({ open: false, department: null });
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [manualNameMode, setManualNameMode] = useState(false);
  const [manualName, setManualName] = useState('');
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');
  const [newDeptCategory, setNewDeptCategory] = useState('SUPPORT');
  const [addingDept, setAddingDept] = useState(false);
  const token = localStorage.getItem('token');

  // Get the current store ID from localStorage (set by store dropdown)
  const getSelectedStoreId = () => {
    return localStorage.getItem('selectedStoreId') || user?.store_id;
  };

  // Permission checks
  const canToggleRound3 = ['network_admin', 'regional_manager', 'complex_manager', 'store_manager', 'stocktake_controller', 'stocktake_champion'].includes(user?.role);
  const canEditDepartments = user?.role === 'network_admin';
  const canAddDepartments = ['network_admin', 'store_manager', 'stocktake_controller'].includes(user?.role);
  const canResetRounds = ['network_admin', 'regional_manager', 'complex_manager', 'store_manager', 'stocktake_controller'].includes(user?.role);
  const canMarkComplete = ['network_admin', 'regional_manager', 'complex_manager', 'store_manager', 'stocktake_controller', 'stocktake_champion'].includes(user?.role);
  const canManualAssign = ['network_admin', 'regional_manager', 'complex_manager'].includes(user?.role);

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds for timer updates
    const interval = setInterval(fetchData, 30000);
    
    // Listen for store changes
    const handleStoreChange = () => {
      fetchData();
    };
    window.addEventListener('storeChanged', handleStoreChange);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storeChanged', handleStoreChange);
    };
  }, []);

  const fetchData = async () => {
    try {
      const storeId = getSelectedStoreId();
      const storeParam = storeId ? `?store_id=${storeId}` : '';
      
      const [deptRes, assignRes, rosterRes, settingsRes] = await Promise.all([
        fetch(`${API}/verification-departments`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/verification-assignments${storeParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/rosters${storeParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/verification-settings`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setDepartments(await deptRes.json());
      setAssignments(await assignRes.json());
      setRosters(await rosterRes.json());
      if (settingsRes.ok) {
        setSettings(await settingsRes.json());
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Get Wednesday rostered team members
  const wednesdayRosters = rosters.filter(r => r.day === 'Wednesday');

  // Get stats for a round
  const getRoundStats = (roundNumber) => {
    const activeDepartments = departments.filter(d => d.is_active);
    const roundAssignments = assignments.filter(a => a.round_number === roundNumber);
    
    // Available = not assigned yet
    const assignedDeptIds = roundAssignments.map(a => a.department_id);
    const available = activeDepartments.filter(d => !assignedDeptIds.includes(d.id)).length;
    
    // Taken/To Be Keyed = assigned and in progress (has timer running)
    const toBeKeyed = roundAssignments.filter(a => a.status === 'taken').length;
    
    // Complete = marked complete by controller
    const complete = roundAssignments.filter(a => a.status === 'complete').length;
    
    const total = activeDepartments.length;
    const percentage = total > 0 ? Math.round((complete / total) * 100) : 0;

    return { available, taken: 0, toBeKeyed, complete, total, percentage };
  };

  // Get assignment for a department in current round
  const getAssignment = (departmentId) => {
    return assignments.find(a => a.round_number === selectedRound && a.department_id === departmentId);
  };

  // Calculate elapsed time
  const getElapsedTime = (startedAt) => {
    if (!startedAt) return '00:00';
    const start = new Date(startedAt);
    const now = new Date();
    const diff = Math.floor((now - start) / 1000);
    const minutes = Math.floor(diff / 60);
    const seconds = diff % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Assign department to team member
  const handleAssign = async () => {
    // Check if using manual name or roster selection
    if (manualNameMode) {
      if (!manualName.trim()) {
        toast.error('Please enter a name');
        return;
      }
    } else {
      if (!selectedAssignee) {
        toast.error('Please select a team member');
        return;
      }
    }

    const roster = !manualNameMode ? wednesdayRosters.find(r => r.id === selectedAssignee) : null;
    const assigneeName = manualNameMode ? manualName.trim() : (roster?.user_name || 'Unknown');
    const assigneeId = manualNameMode ? `manual_${Date.now()}` : (roster?.user_id || selectedAssignee);
    const storeId = getSelectedStoreId();
    
    try {
      const response = await fetch(`${API}/verification-assignments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          round_number: selectedRound,
          department_id: assignDialog.department.id,
          assigned_to: assigneeId,
          assigned_to_name: assigneeName,
          store_id: storeId
        })
      });

      if (response.ok) {
        toast.success(`Assigned to ${assigneeName}`);
        setAssignDialog({ open: false, department: null });
        setSelectedAssignee('');
        setManualName('');
        setManualNameMode(false);
        fetchData();
      } else {
        toast.error('Failed to assign');
      }
    } catch (error) {
      toast.error('Failed to assign');
    }
  };

  // Mark assignment as complete
  const markComplete = async (assignmentId) => {
    try {
      await fetch(`${API}/verification-assignments/${assignmentId}/complete`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Marked as complete');
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  // Mark as to be keyed
  const markToBeKeyed = async (assignmentId) => {
    try {
      await fetch(`${API}/verification-assignments/${assignmentId}/to-be-keyed`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Marked as to be keyed');
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  // Reset a round
  const resetRound = async () => {
    if (!window.confirm(`Are you sure you want to reset Round ${selectedRound}? All assignments will be cleared.`)) {
      return;
    }

    try {
      const storeId = getSelectedStoreId();
      const storeParam = storeId ? `?store_id=${storeId}` : '';
      await fetch(`${API}/verification-assignments/reset/${selectedRound}${storeParam}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Round ${selectedRound} has been reset`);
      fetchData();
    } catch (error) {
      toast.error('Failed to reset round');
    }
  };

  // Toggle Round 3
  const toggleRound3 = async (enabled) => {
    try {
      await fetch(`${API}/verification-settings?round_3_enabled=${enabled}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({ ...settings, round_3_enabled: enabled });
      toast.success(`Round 3 ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  // Toggle department active status
  const toggleDepartment = async (deptId, isActive) => {
    try {
      await fetch(`${API}/verification-departments/${deptId}?is_active=${isActive}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      toast.success('Department updated');
    } catch (error) {
      toast.error('Failed to update department');
    }
  };

  // Add new department
  const handleAddDepartment = async () => {
    if (!newDeptName.trim()) {
      toast.error('Please enter a department name');
      return;
    }
    
    setAddingDept(true);
    try {
      const response = await fetch(`${API}/verification-departments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newDeptName.trim(),
          category: newDeptCategory
        })
      });
      
      if (response.ok) {
        toast.success(`Department "${newDeptName}" added successfully`);
        setNewDeptName('');
        setNewDeptCategory('SUPPORT');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add department');
      }
    } catch (error) {
      toast.error('Failed to add department');
    } finally {
      setAddingDept(false);
    }
  };

  // Delete department
  const handleDeleteDepartment = async (deptId, deptName) => {
    if (!window.confirm(`Are you sure you want to delete "${deptName}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API}/verification-departments/${deptId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success(`Department "${deptName}" deleted`);
        fetchData();
      } else {
        toast.error('Failed to delete department');
      }
    } catch (error) {
      toast.error('Failed to delete department');
    }
  };

  // Group departments by category
  const getDepartmentsByCategory = () => {
    const grouped = {};
    const activeDepartments = departments.filter(d => d.is_active);
    
    activeDepartments
      .filter(d => 
        d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.category.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .forEach(dept => {
        if (!grouped[dept.category]) {
          grouped[dept.category] = [];
        }
        grouped[dept.category].push(dept);
      });

    // Sort each category by sort_order
    Object.keys(grouped).forEach(cat => {
      grouped[cat].sort((a, b) => a.sort_order - b.sort_order);
    });

    return grouped;
  };

  // Render Round Overview (main page)
  if (!selectedRound) {
    const totalStats = {
      available: 0,
      taken: 0,
      toBeKeyed: 0,
      complete: 0
    };

    const rounds = settings.round_3_enabled ? [1, 2, 3] : [1, 2];
    
    rounds.forEach(r => {
      const stats = getRoundStats(r);
      totalStats.available += stats.available;
      totalStats.taken += stats.taken;
      totalStats.toBeKeyed += stats.toBeKeyed;
      totalStats.complete += stats.complete;
    });

    return (
      <div className="space-y-8" data-testid="verifications-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight mb-2">Verifications</h1>
            <p className="text-slate-600">Wednesday verification rounds</p>
          </div>
          {(canToggleRound3 || canEditDepartments) && (
            <Button 
              variant="outline" 
              onClick={() => setSettingsDialogOpen(true)}
              data-testid="verification-settings-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
          )}
        </div>

        {/* Round Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {rounds.map(roundNum => {
            const stats = getRoundStats(roundNum);
            return (
              <div
                key={roundNum}
                className="bg-white rounded-xl border-2 border-slate-200 p-6 hover:border-teal-300 hover:shadow-lg transition-all cursor-pointer"
                onClick={() => setSelectedRound(roundNum)}
                data-testid={`round-${roundNum}-card`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-slate-900">Round {roundNum}</h2>
                  <Badge className={stats.percentage === 100 ? 'bg-green-500' : 'bg-slate-200 text-slate-700'}>
                    {stats.percentage}%
                  </Badge>
                </div>
                <div className="text-lg text-slate-600 mb-4">
                  {stats.complete}/{stats.total} complete
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${stats.percentage === 100 ? 'bg-green-500' : 'bg-teal-500'}`}
                    style={{ width: `${stats.percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary Stats */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-3xl font-bold text-slate-600">{totalStats.available}</p>
              <p className="text-sm text-slate-500">Available</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-3xl font-bold text-blue-600">{totalStats.taken}</p>
              <p className="text-sm text-slate-500">Taken</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-lg">
              <p className="text-3xl font-bold text-amber-600">{totalStats.toBeKeyed}</p>
              <p className="text-sm text-slate-500">To Be Keyed</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">{totalStats.complete}</p>
              <p className="text-sm text-slate-500">Complete</p>
            </div>
          </div>
        </div>

        {/* Settings Dialog */}
        <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Verification Settings</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Round 3 Toggle */}
              {canToggleRound3 && (
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="text-base font-semibold">Round 3</Label>
                    <p className="text-sm text-slate-500">Enable or disable Round 3 verification</p>
                  </div>
                  <Switch
                    checked={settings.round_3_enabled}
                    onCheckedChange={toggleRound3}
                    data-testid="round-3-toggle"
                  />
                </div>
              )}

              {/* Add New Department */}
              {canAddDepartments && (
                <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                  <h4 className="font-semibold text-teal-900 mb-3">Add New Department</h4>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm mb-1 block">Department Name</Label>
                      <Input
                        placeholder="Enter department name..."
                        value={newDeptName}
                        onChange={(e) => setNewDeptName(e.target.value)}
                        data-testid="new-dept-name"
                      />
                    </div>
                    <div>
                      <Label className="text-sm mb-1 block">Category</Label>
                      <Select value={newDeptCategory} onValueChange={setNewDeptCategory}>
                        <SelectTrigger data-testid="new-dept-category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="BUILDERS">BUILDERS</SelectItem>
                          <SelectItem value="LIFESTYLES">LIFESTYLES</SelectItem>
                          <SelectItem value="IN THE HOME">IN THE HOME</SelectItem>
                          <SelectItem value="SUPPORT">SUPPORT</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      onClick={handleAddDepartment}
                      disabled={addingDept || !newDeptName.trim()}
                      className="bg-teal-600 hover:bg-teal-700 w-full"
                      data-testid="add-dept-btn"
                    >
                      {addingDept ? 'Adding...' : 'Add Department'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Department Management */}
              {(canEditDepartments || canAddDepartments) && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Manage Departments</h4>
                  <p className="text-sm text-slate-500 mb-4">
                    {canEditDepartments 
                      ? 'Toggle departments on/off or delete custom departments'
                      : 'View and delete custom departments'}
                  </p>
                  
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {Object.entries(categoryColors).map(([category, colors]) => (
                      <div key={category}>
                        <div className={`px-3 py-1 font-semibold text-sm text-white ${colors.header} rounded-t-lg`}>
                          {category}
                        </div>
                        <div className="border border-t-0 rounded-b-lg divide-y">
                          {departments
                            .filter(d => d.category === category)
                            .sort((a, b) => a.sort_order - b.sort_order)
                            .map(dept => (
                              <div key={dept.id} className="flex items-center justify-between px-4 py-2">
                                <span className={dept.is_active ? 'text-slate-900' : 'text-slate-400'}>{dept.name}</span>
                                <div className="flex items-center gap-2">
                                  {canEditDepartments && (
                                    <button
                                      onClick={() => toggleDepartment(dept.id, !dept.is_active)}
                                      className="p-1 hover:bg-slate-100 rounded"
                                      data-testid={`toggle-dept-${dept.id}`}
                                    >
                                      {dept.is_active ? (
                                        <Eye className="w-4 h-4 text-green-600" />
                                      ) : (
                                        <EyeOff className="w-4 h-4 text-slate-400" />
                                      )}
                                    </button>
                                  )}
                                  {canAddDepartments && dept.created_by && (
                                    <button
                                      onClick={() => handleDeleteDepartment(dept.id, dept.name)}
                                      className="p-1 hover:bg-red-100 rounded text-red-500 hover:text-red-700"
                                      title="Delete department"
                                      data-testid={`delete-dept-${dept.id}`}
                                    >
                                      <span className="text-xs font-bold">×</span>
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Render Round Detail Page
  const stats = getRoundStats(selectedRound);
  const groupedDepartments = getDepartmentsByCategory();

  return (
    <div className="space-y-6" data-testid="round-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => setSelectedRound(null)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">ROUND {selectedRound}</h1>
            <p className="text-slate-500">{stats.complete}/{stats.total} complete • {stats.percentage}%</p>
          </div>
        </div>
        {canResetRounds && (
          <Button 
            variant="outline" 
            onClick={resetRound}
            className="text-red-600 border-red-200 hover:bg-red-50"
            data-testid="reset-round-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset Round
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Search sub-department or person..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-input"
        />
      </div>

      {/* Department List by Category */}
      <div className="space-y-6">
        {Object.entries(groupedDepartments).map(([category, depts]) => {
          const colors = categoryColors[category] || categoryColors['BUILDERS'];
          
          return (
            <div key={category} className="space-y-2">
              <div className={`px-4 py-2 font-bold text-white ${colors.header} rounded-lg`}>
                {category}
              </div>
              
              <div className="space-y-2">
                {depts.map(dept => {
                  const assignment = getAssignment(dept.id);
                  const isAssigned = assignment && assignment.status === 'taken';
                  const isComplete = assignment?.status === 'complete';

                  return (
                    <div 
                      key={dept.id}
                      className={`flex items-center justify-between p-4 rounded-lg border ${colors.border} ${colors.bg}`}
                      data-testid={`dept-${dept.id}`}
                    >
                      <div className="flex-1">
                        <h4 className="font-medium text-slate-900">{dept.name}</h4>
                        {isAssigned && !isComplete ? (
                          <div className="flex items-center gap-2 mt-1">
                            <User className="w-4 h-4 text-slate-500" />
                            <span className="text-sm text-slate-600">{assignment.assigned_to_name}</span>
                          </div>
                        ) : isComplete ? (
                          <div className="flex items-center gap-2 mt-1">
                            <User className="w-4 h-4 text-green-500" />
                            <span className="text-sm text-green-600">{assignment.assigned_to_name}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-slate-500">Ready to assign</span>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {isComplete ? (
                          <Badge className="bg-green-500 text-white">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Complete
                          </Badge>
                        ) : isAssigned ? (
                          <div className="flex items-center gap-2">
                            <Badge className="bg-amber-500 text-white">
                              <Clock className="w-3 h-3 mr-1" />
                              To Be Keyed
                            </Badge>
                            <span className="text-sm font-mono text-slate-600">
                              {getElapsedTime(assignment.started_at)}
                            </span>
                            {canMarkComplete && (
                              <Button
                                size="sm"
                                onClick={() => markComplete(assignment.id)}
                                className="bg-green-600 hover:bg-green-700"
                                data-testid={`complete-${dept.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Complete
                              </Button>
                            )}
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => setAssignDialog({ open: true, department: dept })}
                            className="bg-teal-600 hover:bg-teal-700"
                            data-testid={`assign-${dept.id}`}
                          >
                            Assign
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Assign Dialog */}
      <Dialog open={assignDialog.open} onOpenChange={(open) => {
        setAssignDialog({ ...assignDialog, open });
        if (!open) {
          setManualNameMode(false);
          setManualName('');
          setSelectedAssignee('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign {assignDialog.department?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Toggle between roster selection and manual entry */}
            {canManualAssign && (
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                <Switch
                  checked={manualNameMode}
                  onCheckedChange={(checked) => {
                    setManualNameMode(checked);
                    setSelectedAssignee('');
                    setManualName('');
                  }}
                  data-testid="manual-name-toggle"
                />
                <Label className="text-sm">
                  {manualNameMode ? 'Manual Entry Mode' : 'Select from Roster'}
                </Label>
                {manualNameMode && (
                  <span className="text-xs text-amber-600 ml-auto">Person not on attendance</span>
                )}
              </div>
            )}

            {manualNameMode ? (
              <div>
                <Label className="mb-2 block">Enter Team Member Name</Label>
                <Input
                  placeholder="Enter name manually..."
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                  data-testid="manual-name-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Use this for team members not on the attendance roster
                </p>
              </div>
            ) : (
              <div>
                <Label className="mb-2 block">Select Team Member (Wednesday Roster)</Label>
                {wednesdayRosters.length === 0 ? (
                  <div className="text-sm text-slate-500 p-4 bg-slate-50 rounded-lg">
                    <p>No team members rostered for Wednesday.</p>
                    {canManualAssign && (
                      <p className="mt-2 text-teal-600">Toggle "Manual Entry Mode" above to enter a name manually.</p>
                    )}
                  </div>
                ) : (
                  <Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
                    <SelectTrigger data-testid="assignee-select">
                      <SelectValue placeholder="Select team member..." />
                    </SelectTrigger>
                    <SelectContent>
                      {wednesdayRosters.map(roster => (
                        <SelectItem key={roster.id} value={roster.id}>
                          {roster.user_name} {roster.team_member_number && `(#${roster.team_member_number})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setAssignDialog({ open: false, department: null })}>
                Cancel
              </Button>
              <Button 
                onClick={handleAssign} 
                className="bg-teal-600 hover:bg-teal-700"
                disabled={manualNameMode ? !manualName.trim() : (!selectedAssignee && wednesdayRosters.length > 0)}
                data-testid="confirm-assign-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                Start Timer & Assign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Verifications;
